import BlzButton from '@berlitz/button'
import Spacer from '@berlitz/spacer'
import Calendar1 from '@berlitz/streamline-icons/lib/regular/01-Interface-Essential/21-Date-Calendar/Calendar1'
import LearningPathUnit from '@classes/LearningPathUnit'
import JoinLessonButton from '@components/JoinLessonButton'
import { Card, CardContent, CardHeader, Icon, IconCircle, Title } from '@components/LPCard/Common'
import usePortalFeatures from '@components/RoleBasedView/usePortalFeatures'
import WithUserTime, { WithUserTimeProps } from '@hocs/WithUserTime'
import { DATE_FORMAT } from '@utils/constants'
import Router from 'next/router'
import React from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { TopTag } from '../Lesson/style'
import { Link, SubContainer, SubDescription } from './style'

interface JoinOptionProps extends WithUserTimeProps, InjectedIntlProps {
  Unit: LearningPathUnit
}

export const JoinOption: React.FC<JoinOptionProps> = ({ getDateWithUserTimezone, Unit }) => {
  const activeUnit = Unit
  const lesson = activeUnit?.Session?.lesson
  const startTime = activeUnit?.Session?.StartTime || lesson?.StartTime
  const sessionStartTime = getDateWithUserTimezone(startTime)
  const isPastLesson = sessionStartTime.isValid() && sessionStartTime.isBefore(getDateWithUserTimezone())

  const { state, features } = usePortalFeatures({
    configId: 'c::newLearningExperience',
  })

  return (
    <Card type="speakingPanel">
      <CardContent isElective={Unit?.IsFromElective}>
        <CardHeader>
          {Unit?.IsFromElective && (
            <TopTag color="info100">
              <FormattedMessage id="Elective" defaultMessage="Elective" />
            </TopTag>
          )}
          <Icon color="white">
            <IconCircle color="info100" />
            <Calendar1 />
          </Icon>
        </CardHeader>
        <Spacer size="md" />
        <Title color="info100">
          {lesson?.UnitId ? activeUnit?.LCSTitle || '' : 'Live Coaching Session'}{' '}
          {!startTime && isPastLesson && (
            <span className="lowercase">
              <FormattedMessage id="Completed" />
            </span>
          )}
        </Title>
        <Spacer size="sm" />
        <SubContainer shadowed>
          {startTime && (
            <>
              <SubDescription color="charcoal">
                <Calendar1 />
                <span>{sessionStartTime.format(DATE_FORMAT.COMPLETE_24)}</span>
              </SubDescription>
              <Spacer size="sm" />
            </>
          )}
          <JoinLessonButton title="Join" className="join-lesson" lesson={lesson} fullWidth />
          <Spacer size="sm" />
          {
            <>
              <BlzButton
                className="narrow"
                outline
                fullWidth
                disabled={!activeUnit?.EntryPointUrl}
                onClick={() => {
                  if (state.allowed) {
                    return features.cr8.redirectToCR8(activeUnit?.LearningPathId ?? '', activeUnit?.EntryPointUrl)
                  }

                  window.open(activeUnit?.EntryPointUrl, '_self')
                }}
              >
                <FormattedMessage id="Performance Card" />
              </BlzButton>
              <Spacer size="xxs" />
            </>
          }
          {activeUnit?.ActionPlanUrl && (
            <BlzButton
              className="narrow"
              outline
              fullWidth
              onClick={() => {
                if (state.allowed) {
                  return features.cr8.redirectToCR8(activeUnit?.LearningPathId ?? '', activeUnit?.ActionPlanUrl)
                }

                window.open(activeUnit?.ActionPlanUrl, '_self')
              }}
            >
              <FormattedMessage id="Action Plan" />
            </BlzButton>
          )}
          <Spacer size="sm" />
          <Link onClick={() => Router.push(`/scheduling/my-calendar/`)}>
            <FormattedMessage id="View Calendar" />
          </Link>
          <Spacer size="md" />
        </SubContainer>
      </CardContent>
    </Card>
  )
}

export default WithUserTime(injectIntl(JoinOption))
